import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import type { Cue } from '@core/model/cue.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { SubtitleProvider } from '../ports/SubtitleProvider.ts';
import {
  SubtitleStore,
  type RawTrackType,
  type SubtitleSlot,
} from '../state/subtitleStore.ts';

import { pickPreferredTrackValue, rememberLanguagePreference, shouldSkipLanguageMru, shouldSkipPreferredSelection } from '../lib/trackPreference.ts';
import { resolvePreprocessFlags } from '@core/preprocess.ts';
import { preprocessTrackCues } from '../lib/preprocessTrack.ts';
import { applyFuriganaToCues, cueProcessSettingsKey } from '../features/furigana/generateFurigana.ts';

import {
  parseSubtitleFile,
  type FileUploadSubtitleProvider,
} from '../adapters/subtitles/FileUploadSubtitleProvider.ts';
import type { TrackList } from '@core/model/track.ts';
import { applyDualViewFromSettings } from '../lib/bilingualDefaults.ts';
import { translationsForEngine } from '../lib/translationLanguages.ts';
import {
  CaptionJobCancelledError,
  clearMemberEnhanceState,
} from '../adapters/net/memberJobs.ts';
import { normalizeSubtitleOptimizeModel } from '@core/alignment/subtitleOptimizeModel.ts';
import { resolveYoutubeIndexAfterOptimizeToggle } from '@core/selection/remapAfterOptimizeToggle.ts';
import {
  infoCue,
  serviceLoadingText,
} from '../lib/serviceStatusMessages.ts';
import {
  maybeApplyLanguageStyleRuntime,
  reapplyCustomStylesForSelectedSlots,
} from '../lib/applyLanguageStyleRuntime.ts';

/** Extension parity: uploaded file appears as a fixed "custom" track (value 9999). */
export const UPLOADED_TRACK_VALUE = 9999;
export const UPLOADED_TRACK_CODE = 'custom';

export type UseSubtitleLoaderResult = {
  /** True when listTracks returned no subtitles for the current video. */
  empty: boolean;
  listing: boolean;
  /** Last download/list error message (per latest operation). */
  error: string | null;
  /**
   * Job / download progress text (Enhance / Whisper / download).
   * Painted on the subtitle track via info cues; kept for callers/tests.
   */
  progressMessage: string | null;
  /** True while listing tracks or any slot is loading. */
  busy: boolean;
  selectTrack: (slot: SubtitleSlot, trackId: string) => Promise<void>;
  /** Re-run listTracks (e.g. after upload attach). */
  refreshTracks: () => Promise<void>;
  /** Import a local file into a slot (Settings Import; extension uploadSRT). */
  importFile: (slot: SubtitleSlot, file: File, label?: string) => Promise<void>;
  clearError: () => void;
};

/** Write extension-style status cue onto the overlay track for this slot. */
function paintSlotProgress(
  store: SubtitleStore,
  slot: SubtitleSlot,
  message: string,
  onProgress?: (message: string) => void,
): void {
  store.setCues(slot, infoCue(message));
  onProgress?.(message);
}

function ensureUploadedTrack(list: TrackList, label: string): TrackList {
  const subtitles = [...list.subtitles];
  const idx = subtitles.findIndex(
    (t) => t.value === UPLOADED_TRACK_VALUE || t.code === UPLOADED_TRACK_CODE,
  );
  if (idx >= 0) {
    subtitles[idx] = {
      ...subtitles[idx]!,
      name: label,
      value: UPLOADED_TRACK_VALUE,
      code: UPLOADED_TRACK_CODE,
      downloadable: true,
    };
  } else {
    subtitles.push({
      name: label,
      value: UPLOADED_TRACK_VALUE,
      code: UPLOADED_TRACK_CODE,
      downloadable: true,
    });
  }
  return { ...list, subtitles };
}

function hasUploadedCues(
  uploaded: Record<SubtitleSlot, Cue[] | undefined>,
): boolean {
  return Boolean(uploaded.main?.length || uploaded.second?.length);
}

function preprocessFlagsFromSettings(settingsStore: SettingsStore) {
  const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
  return resolvePreprocessFlags(settings);
}

async function storeProcessedCues(
  store: SubtitleStore,
  slot: SubtitleSlot,
  raw: Cue[],
  settingsStore: SettingsStore,
  languageCode?: string,
  trackType: RawTrackType = 'text',
): Promise<void> {
  const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
  const processed = preprocessTrackCues(
    raw,
    preprocessFlagsFromSettings(settingsStore),
    trackType,
  );
  const withFuri = await applyFuriganaToCues(processed, settings, languageCode);
  store.setCues(slot, withFuri, { type: trackType });
}

function maybeApplyLanguageStyle(
  settingsStore: SettingsStore,
  slot: SubtitleSlot,
  languageCode: string | undefined,
): void {
  maybeApplyLanguageStyleRuntime(settingsStore, slot, languageCode);
}

type TrackRow = TrackList['subtitles'][number] & {
  enhanced?: boolean;
  creatorEnhanced?: boolean;
  whisper?: boolean;
  vssId?: string;
  kind?: string;
};

type OptimizeSelSnap = {
  value: string;
  code?: string;
  enhanced?: boolean;
  creatorEnhanced?: boolean;
  whisper?: boolean;
  kind?: string;
};

/** Map provider id → enhance/platform gate id (extension wireSubtitlePipeline). */
function platformForProvider(provider: SubtitleProvider | null | undefined): string {
  const id = provider?.id;
  if (id === 'sora' || id === 'mock') return 'youtube';
  if (id === 'upload') return 'upload';
  return id || 'upload';
}

function snapSelectedTrack(
  store: SubtitleStore,
  slot: SubtitleSlot,
): OptimizeSelSnap | null {
  const { selectedId, trackList } = store.getSlot(slot);
  if (!selectedId) return null;
  const track = trackList?.subtitles.find((t) => String(t.value) === selectedId) as
    | TrackRow
    | undefined;
  if (!track) return { value: selectedId };
  return {
    value: selectedId,
    code: track.code,
    enhanced: track.enhanced,
    creatorEnhanced: track.creatorEnhanced,
    whisper: track.whisper,
    kind: track.kind,
  };
}

/** Keep prior pick across optimize reinject; map type-2/3 → plain when removed. */
function resolveTrackIdAfterOptimizeToggle(
  list: TrackList,
  snap: OptimizeSelSnap | null,
): string | undefined {
  if (!snap) return undefined;
  if (list.subtitles.some((t) => String(t.value) === snap.value)) {
    return snap.value;
  }
  const rows = list.subtitles as TrackRow[];
  const idx = resolveYoutubeIndexAfterOptimizeToggle(
    rows.map((t) => ({
      languageCode: t.code,
      enhanced: t.enhanced,
      creatorEnhanced: t.creatorEnhanced,
      whisper: t.whisper,
      kind: t.kind,
    })),
    {
      languageCode: snap.code,
      enhanced: snap.enhanced,
      creatorEnhanced: snap.creatorEnhanced,
      whisper: snap.whisper,
      kind: snap.kind,
    },
  );
  if (idx === undefined) return undefined;
  return String(rows[idx]!.value);
}

/**
 * Method 3 is menu-only: providers enhance when the user picks an Optimized /
 * Enhanced row. Never silently rewrite a plain language download.
 */
async function applySubtitleOptimizeIfNeeded(
  cues: Cue[],
  _videoId: string,
  _track: TrackRow | undefined,
  _settingsStore: SettingsStore,
  _platform: string,
  _onProgress?: (message: string) => void,
  _shouldContinue?: () => boolean,
): Promise<Cue[]> {
  return cues;
}

function trackLanguage(
  store: SubtitleStore,
  slot: SubtitleSlot,
  trackId: string | undefined,
): string | undefined {
  if (!trackId) return undefined;
  const list = store.getSlot(slot).trackList;
  return list?.subtitles.find((t) => String(t.value) === trackId)?.code;
}

async function autoPickAndLoad(
  provider: SubtitleProvider,
  videoId: string,
  store: SubtitleStore,
  settingsStore: SettingsStore,
  slot: SubtitleSlot,
  originalRef: MutableRefObject<Record<SubtitleSlot, Cue[] | undefined>>,
  onProgress?: (message: string) => void,
  uploadedRef?: MutableRefObject<Record<SubtitleSlot, Cue[] | undefined>>,
  shouldContinue?: () => boolean,
): Promise<void> {
  const settings = settingsStore.getSnapshot?.() ?? (await settingsStore.getAll());
  const list = store.getSlot(slot).trackList;
  if (!list || list.subtitles.length === 0) return;

  const mru = slot === 'main' ? settings.mainLanguages : settings.secondLanguages;
  // User chose "—" (extension `null`) or empty MRU — never invent a pick.
  if (shouldSkipPreferredSelection(mru)) return;

  const preferred = pickPreferredTrackValue(list.subtitles, mru);
  if (preferred === undefined) return;

  const trackId = String(preferred);
  store.select(slot, trackId);
  store.setViewState(slot, { hidden: false });
  store.setStatus(slot, 'loading');
  // Extension writeDownloaded Loading… placeholder on the track before network.
  paintSlotProgress(store, slot, serviceLoadingText(), onProgress);
  try {
    const uploaded = uploadedRef?.current[slot];
    const cuesRaw =
      Number(trackId) === UPLOADED_TRACK_VALUE && uploaded?.length
        ? uploaded
        : await provider.downloadTrack(videoId, trackId, {
            onProgress: (message) => paintSlotProgress(store, slot, message, onProgress),
            shouldContinue,
          });
    if (shouldContinue && !shouldContinue()) return;
    const track = list.subtitles.find((t) => t.value === preferred) as TrackRow | undefined;
    const cues = await applySubtitleOptimizeIfNeeded(
      cuesRaw,
      videoId,
      track,
      settingsStore,
      platformForProvider(provider),
      (message) => paintSlotProgress(store, slot, message, onProgress),
      shouldContinue,
    );
    if (shouldContinue && !shouldContinue()) return;
    originalRef.current[slot] = cues;
    await storeProcessedCues(store, slot, cues, settingsStore, track?.code);
    // Extension preferred pick does not rewrite MRU; only manual select / import does.
    if (track?.code) {
      maybeApplyLanguageStyle(settingsStore, slot, track.code);
    }
  } catch (err) {
    if (err instanceof CaptionJobCancelledError) return;
    originalRef.current[slot] = undefined;
    store.setCues(slot, undefined);
    store.setStatus(slot, 'error');
    throw err;
  }
}

/**
 * Load track lists + cues from a SubtitleProvider into SubtitleStore.
 * Resets the store when videoId changes.
 */
export function useSubtitleLoader(
  provider: SubtitleProvider | null,
  videoId: string | null,
  store: SubtitleStore,
  settingsStore: SettingsStore,
): UseSubtitleLoaderResult {
  const [empty, setEmpty] = useState(false);
  const [listing, setListing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [slotBusy, setSlotBusy] = useState(false);
  const genRef = useRef(0);
  const originalRef = useRef<Record<SubtitleSlot, Cue[] | undefined>>({
    main: undefined,
    second: undefined,
  });
  /** Per-slot cues from Settings import (extension uploaded.mainParsed / secondParsed). */
  const uploadedRef = useRef<Record<SubtitleSlot, Cue[] | undefined>>({
    main: undefined,
    second: undefined,
  });
  const uploadedLabelRef = useRef('Uploaded subtitles');
  const furiGenRef = useRef(0);

  useEffect(() => {
    const syncBusy = () => {
      const main = store.getSlot('main').status;
      const second = store.getSlot('second').status;
      setSlotBusy(main === 'loading' || second === 'loading');
    };
    syncBusy();
    return store.subscribe(syncBusy);
  }, [store]);

  const refreshTracks = useCallback(async (opts?: { mode?: 'bootstrap' | 'optimize' }) => {
    if (!provider || !videoId) {
      setEmpty(false);
      return;
    }
    const mode = opts?.mode ?? 'bootstrap';
    const gen = ++genRef.current;
    setListing(true);
    setError(null);
    setProgressMessage(null);

    const snapMain = mode === 'optimize' ? snapSelectedTrack(store, 'main') : null;
    const snapSecond = mode === 'optimize' ? snapSelectedTrack(store, 'second') : null;

    try {
      const rawList = await provider.listTracks(videoId);
      if (gen !== genRef.current) return;

      const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
      const withEngineTranslations: TrackList = {
        ...rawList,
        // Extension: translation options come from engine language tables, not metadata.
        translations: translationsForEngine(settings.translationEngine, rawList.subtitles),
      };
      const list = hasUploadedCues(uploadedRef.current)
        ? ensureUploadedTrack(withEngineTranslations, uploadedLabelRef.current)
        : withEngineTranslations;

      store.setTrackList('main', list);
      store.setTrackList('second', list);
      applyDualViewFromSettings(store, settingsStore, {
        translationCodes: (list.translations ?? []).map((tr) => tr.code),
      });
      const isEmpty = list.subtitles.length === 0;
      setEmpty(isEmpty);
      // End listing phase before download so the chip switches to download copy.
      setListing(false);

      if (!isEmpty) {
        try {
          const onProgress = (message: string) => {
            if (gen === genRef.current) setProgressMessage(message);
          };
          setProgressMessage(serviceLoadingText());

          if (mode === 'optimize') {
            // Re-list then re-download remapped selection (avoid dual-subscriber race).
            const stillCurrent = () => gen === genRef.current;
            for (const [slot, snap] of [
              ['main', snapMain],
              ['second', snapSecond],
            ] as const) {
              if (!stillCurrent()) return;
              const resolved = resolveTrackIdAfterOptimizeToggle(list, snap);
              if (resolved) {
                store.select(slot, resolved);
                store.setStatus(slot, 'loading');
                paintSlotProgress(store, slot, serviceLoadingText(), onProgress);
                const isCustom = Number(resolved) === UPLOADED_TRACK_VALUE;
                const uploaded = uploadedRef.current[slot];
                const cuesRaw =
                  isCustom && uploaded?.length
                    ? uploaded
                    : isCustom
                      ? []
                      : await provider.downloadTrack(videoId, resolved, {
                          onProgress: (message) =>
                            paintSlotProgress(store, slot, message, onProgress),
                          shouldContinue: stillCurrent,
                        });
                if (!stillCurrent()) return;
                if (isCustom && !cuesRaw.length) {
                  originalRef.current[slot] = undefined;
                  store.setCues(slot, undefined);
                  store.setStatus(slot, 'idle');
                  continue;
                }
                const track = list.subtitles.find((t) => String(t.value) === resolved) as
                  | TrackRow
                  | undefined;
                const cues = await applySubtitleOptimizeIfNeeded(
                  cuesRaw,
                  videoId,
                  track,
                  settingsStore,
                  platformForProvider(provider),
                  (message) => paintSlotProgress(store, slot, message, onProgress),
                  stillCurrent,
                );
                if (!stillCurrent()) return;
                originalRef.current[slot] = cues;
                await storeProcessedCues(store, slot, cues, settingsStore, track?.code);
                if (track?.code) {
                  maybeApplyLanguageStyle(settingsStore, slot, track.code);
                }
              } else {
                await autoPickAndLoad(
                  provider,
                  videoId,
                  store,
                  settingsStore,
                  slot,
                  originalRef,
                  onProgress,
                  uploadedRef,
                  stillCurrent,
                );
              }
            }
          } else {
            const stillCurrent = () => gen === genRef.current;
            await autoPickAndLoad(
              provider,
              videoId,
              store,
              settingsStore,
              'main',
              originalRef,
              onProgress,
              uploadedRef,
              stillCurrent,
            );
            await autoPickAndLoad(
              provider,
              videoId,
              store,
              settingsStore,
              'second',
              originalRef,
              onProgress,
              uploadedRef,
              stillCurrent,
            );
          }
        } catch (err) {
          if (gen !== genRef.current) return;
          setError(err instanceof Error ? err.message : 'Failed to load preferred track');
        }
      }
    } catch (err) {
      if (gen !== genRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to list tracks');
      setEmpty(true);
    } finally {
      if (gen === genRef.current) {
        setListing(false);
        setProgressMessage(null);
      }
    }
  }, [provider, videoId, store, settingsStore]);

  useEffect(() => {
    store.reset();
    applyDualViewFromSettings(store, settingsStore);
    originalRef.current = { main: undefined, second: undefined };
    uploadedRef.current = { main: undefined, second: undefined };
    setError(null);
    setEmpty(false);
    setProgressMessage(null);
    clearMemberEnhanceState();
    if (!provider || !videoId) return;
    void refreshTracks();
  }, [provider, videoId, store, settingsStore, refreshTracks]);

  useEffect(() => {
    // Extension kuroshiro.js only clears/rebuilds when furigana* change; also
    // re-run when preprocess flags change (same pipeline as storeProcessedCues).
    let prevKey = cueProcessSettingsKey(
      settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
    );
    return settingsStore.subscribe(() => {
      const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
      const nextKey = cueProcessSettingsKey(settings);
      if (nextKey === prevKey) return;
      prevKey = nextKey;
      const gen = ++furiGenRef.current;
      void (async () => {
        for (const slot of ['main', 'second'] as const) {
          const raw = originalRef.current[slot];
          if (!raw?.length) continue;
          const selected = store.getSlot(slot).selectedId;
          const code = trackLanguage(store, slot, selected);
          const trackType = store.getSlot(slot).rawType ?? 'text';
          await storeProcessedCues(store, slot, raw, settingsStore, code, trackType);
          if (gen !== furiGenRef.current) return;
        }
      })();
    });
  }, [store, settingsStore]);

  // Extension reapplyCustomStylesForSelectedSlots when Style Studio updates bindings.
  useEffect(() => {
    let prevPriorities = JSON.stringify(
      settingsStore.getSnapshot?.()?.styleLanguagePriorities
        ?? DEFAULT_SETTINGS.styleLanguagePriorities,
    );
    let prevCustoms = JSON.stringify(
      settingsStore.getSnapshot?.()?.customStylePresets
        ?? DEFAULT_SETTINGS.customStylePresets,
    );
    return settingsStore.subscribe(() => {
      const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
      const nextPriorities = JSON.stringify(settings.styleLanguagePriorities);
      const nextCustoms = JSON.stringify(settings.customStylePresets);
      if (nextPriorities === prevPriorities && nextCustoms === prevCustoms) return;
      prevPriorities = nextPriorities;
      prevCustoms = nextCustoms;
      reapplyCustomStylesForSelectedSlots(store, settingsStore);
    });
  }, [store, settingsStore]);

  // Extension startTranslationEngineController: rebuild translation options when engine changes.
  useEffect(() => {
    let prevEngine = settingsStore.getSnapshot?.()?.translationEngine
      ?? DEFAULT_SETTINGS.translationEngine;
    return settingsStore.subscribe(() => {
      const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
      const nextEngine = settings.translationEngine || 'google';
      if (nextEngine === prevEngine) return;
      prevEngine = nextEngine;
      for (const slot of ['main', 'second'] as const) {
        const current = store.getSlot(slot).trackList;
        if (!current) continue;
        store.setTrackList(slot, {
          ...current,
          translations: translationsForEngine(nextEngine, current.subtitles),
        });
      }
    });
  }, [store, settingsStore]);

  // Optimize toggle / model change: reinject list then remap/re-download (extension parity).
  useEffect(() => {
    const snap = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
    let prev = snap.subtitleOptimize ?? DEFAULT_SETTINGS.subtitleOptimize;
    let prevModel = normalizeSubtitleOptimizeModel(snap.subtitleOptimizeModel);
    return settingsStore.subscribe(() => {
      const settings = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
      const next = settings.subtitleOptimize ?? DEFAULT_SETTINGS.subtitleOptimize;
      const nextModel = normalizeSubtitleOptimizeModel(settings.subtitleOptimizeModel);
      const optimizeToggled = next !== prev;
      const modelChanged = nextModel !== prevModel;
      if (!optimizeToggled && !modelChanged) return;
      prev = next;
      prevModel = nextModel;
      // Model-only change with optimize off: no Enhanced rows to refresh.
      if (!optimizeToggled && !next) return;
      if (modelChanged) {
        clearMemberEnhanceState();
      }
      void refreshTracks({ mode: 'optimize' });
    });
  }, [settingsStore, refreshTracks]);

  const selectTrack = useCallback(
    async (slot: SubtitleSlot, trackId: string) => {
      if (!provider || !videoId) return;
      const gen = ++genRef.current;
      const stillCurrent = () => gen === genRef.current;
      setError(null);
      store.select(slot, trackId);
      store.setStatus(slot, 'loading');
      // Extension: Loading… info cue on the track before the first onProgress tick.
      const report = (message: string) => {
        if (!stillCurrent()) return;
        setProgressMessage(message);
        paintSlotProgress(store, slot, message);
      };
      report(serviceLoadingText());
      try {
        const isCustom = Number(trackId) === UPLOADED_TRACK_VALUE;
        const uploaded = uploadedRef.current[slot];
        // Extension SubtitlePipeline: custom track reads uploaded.*Parsed only
        // (no network). Missing per-slot upload → empty cues, not downloadTrack(9999).
        if (isCustom && !uploaded?.length) {
          originalRef.current[slot] = undefined;
          store.setCues(slot, undefined);
          store.setStatus(slot, 'idle');
          return;
        }
        const cuesRaw =
          isCustom && uploaded?.length
            ? uploaded
            : await provider.downloadTrack(videoId, trackId, {
                onProgress: report,
                shouldContinue: stillCurrent,
              });
        if (!stillCurrent()) return;
        const list = store.getSlot(slot).trackList;
        const track = list?.subtitles.find((t) => String(t.value) === trackId) as
          | TrackRow
          | undefined;
        const cues = await applySubtitleOptimizeIfNeeded(
          cuesRaw,
          videoId,
          track,
          settingsStore,
          platformForProvider(provider),
          report,
          stillCurrent,
        );
        if (!stillCurrent()) return;
        originalRef.current[slot] = cues;
        report(serviceLoadingText());
        await storeProcessedCues(store, slot, cues, settingsStore, track?.code);
        // Extension saveSelected: skip MRU for custom upload / whisper (no language code).
        if (!shouldSkipLanguageMru(track?.code)) {
          const settings = settingsStore.getSnapshot?.() ?? (await settingsStore.getAll());
          const key = slot === 'main' ? 'mainLanguages' : 'secondLanguages';
          const mru = slot === 'main' ? settings.mainLanguages : settings.secondLanguages;
          await settingsStore.set({ [key]: rememberLanguagePreference(mru, track!.code) });
          maybeApplyLanguageStyle(settingsStore, slot, track!.code);
        }
      } catch (err) {
        if (err instanceof CaptionJobCancelledError || !stillCurrent()) return;
        originalRef.current[slot] = undefined;
        store.setCues(slot, undefined);
        store.setStatus(slot, 'error');
        setError(err instanceof Error ? err.message : 'Failed to download track');
      } finally {
        if (stillCurrent()) setProgressMessage(null);
      }
    },
    [provider, videoId, store, settingsStore],
  );

  const importFile = useCallback(
    async (slot: SubtitleSlot, file: File, label = 'Uploaded subtitles') => {
      setError(null);
      uploadedLabelRef.current = label;
      try {
        const cues = await parseSubtitleFile(file);
        uploadedRef.current[slot] = cues;

        // Prefer the importing slot's list, else the other slot (lists stay synced).
        const other: SubtitleSlot = slot === 'main' ? 'second' : 'main';
        const current =
          store.getSlot(slot).trackList
          ?? store.getSlot(other).trackList
          ?? { subtitles: [], translations: [] };
        const settingsSnap = settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS;
        // After upload there is at least the custom track → engine translation tables load.
        const withUploadPlaceholder = ensureUploadedTrack(current, label);
        const list: TrackList = {
          ...withUploadPlaceholder,
          translations: translationsForEngine(
            settingsSnap.translationEngine,
            withUploadPlaceholder.subtitles,
          ),
        };
        // Extension: one shared selections.subtitles entry visible in both menus.
        store.setTrackList('main', list);
        store.setTrackList('second', list);
        setEmpty(false);

        store.select(slot, String(UPLOADED_TRACK_VALUE));
        store.setViewState(slot, { hidden: false });
        store.setStatus(slot, 'loading');
        // Extension uploadSRT: store cues only — no silent method-3 on custom upload.
        uploadedRef.current[slot] = cues;
        originalRef.current[slot] = cues;
        await storeProcessedCues(store, slot, cues, settingsStore, UPLOADED_TRACK_CODE);
        // Extension uploadSRT selects custom track but does not call saveSelected (no MRU write).
        maybeApplyLanguageStyle(settingsStore, slot, UPLOADED_TRACK_CODE);
      } catch (err) {
        store.setStatus(slot, 'error');
        setError(err instanceof Error ? err.message : 'Failed to import subtitle file');
      }
    },
    [store, settingsStore, videoId],
  );

  return {
    empty,
    listing,
    error,
    progressMessage,
    busy: listing || slotBusy,
    selectTrack,
    refreshTracks,
    importFile,
    clearError: () => setError(null),
  };
}

/** Type guard for upload provider attach API. */
export function isUploadProvider(
  provider: SubtitleProvider | null,
): provider is FileUploadSubtitleProvider {
  return provider?.id === 'upload';
}
